/**
 * ProductSpinViewer - Interactive 360° product spin viewer
 *
 * Drag left/right to rotate the product smoothly.
 * Supports mouse, touch, and keyboard controls.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Loader2, RotateCw, Download, Maximize2, Minimize2, ChevronLeft, ChevronRight, Hand, MousePointer } from 'lucide-react';

interface Props {
  /** Array of frame URLs in order (0° to 360°) */
  frames: string[];
  /** Optional: Degrees per frame for display */
  degreesPerFrame?: number;
  /** Auto-rotate speed (degrees per second, 0 = disabled) */
  autoRotateSpeed?: number;
  /** Initial frame index */
  initialFrame?: number;
  /** Callback when frame changes */
  onFrameChange?: (frameIndex: number, degrees: number) => void;
  /** Show controls overlay */
  showControls?: boolean;
  /** Enable fullscreen mode */
  enableFullscreen?: boolean;
  /** Custom className */
  className?: string;
}

export function ProductSpinViewer({
  frames,
  degreesPerFrame = 360 / frames.length,
  autoRotateSpeed = 0,
  initialFrame = 0,
  onFrameChange,
  showControls = true,
  enableFullscreen = true,
  className = ''
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentFrame, setCurrentFrame] = useState(initialFrame);
  const [isDragging, setIsDragging] = useState(false);
  const [isAutoRotating, setIsAutoRotating] = useState(autoRotateSpeed > 0);
  const [loadedFrames, setLoadedFrames] = useState<Set<number>>(new Set());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showHint, setShowHint] = useState(true);

  // Drag state refs (avoid re-renders during drag)
  const dragStartX = useRef(0);
  const dragStartFrame = useRef(0);
  const lastDragX = useRef(0);
  const velocity = useRef(0);
  const animationFrame = useRef<number>();

  const totalFrames = frames.length;

  // Preload all frames
  useEffect(() => {
    frames.forEach((url, index) => {
      const img = new Image();
      img.onload = () => {
        setLoadedFrames(prev => new Set(prev).add(index));
      };
      img.src = url;
    });
  }, [frames]);

  // Auto-rotate animation
  useEffect(() => {
    if (!isAutoRotating || isDragging) return;

    const interval = setInterval(() => {
      setCurrentFrame(prev => (prev + 1) % totalFrames);
    }, 1000 / (autoRotateSpeed / degreesPerFrame));

    return () => clearInterval(interval);
  }, [isAutoRotating, isDragging, autoRotateSpeed, degreesPerFrame, totalFrames]);

  // Notify parent of frame changes
  useEffect(() => {
    onFrameChange?.(currentFrame, currentFrame * degreesPerFrame);
  }, [currentFrame, degreesPerFrame, onFrameChange]);

  // Hide hint after first interaction
  useEffect(() => {
    if (isDragging) {
      setShowHint(false);
    }
  }, [isDragging]);

  // Momentum animation
  const applyMomentum = useCallback(() => {
    if (Math.abs(velocity.current) < 0.1) {
      velocity.current = 0;
      return;
    }

    velocity.current *= 0.95; // Friction

    const frameChange = velocity.current / 5;
    setCurrentFrame(prev => {
      let next = prev + frameChange;
      // Wrap around
      while (next < 0) next += totalFrames;
      while (next >= totalFrames) next -= totalFrames;
      return Math.round(next);
    });

    animationFrame.current = requestAnimationFrame(applyMomentum);
  }, [totalFrames]);

  // Calculate frame from drag distance
  const calculateFrameFromDrag = useCallback((clientX: number) => {
    const container = containerRef.current;
    if (!container) return dragStartFrame.current;

    const containerWidth = container.offsetWidth;
    const deltaX = clientX - dragStartX.current;

    // Sensitivity: full container width = 180° rotation
    const framesPerPixel = (totalFrames / 2) / containerWidth;
    const frameDelta = Math.round(deltaX * framesPerPixel);

    let newFrame = dragStartFrame.current - frameDelta;

    // Wrap around
    while (newFrame < 0) newFrame += totalFrames;
    while (newFrame >= totalFrames) newFrame -= totalFrames;

    return newFrame;
  }, [totalFrames]);

  // Mouse handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setIsAutoRotating(false);
    dragStartX.current = e.clientX;
    dragStartFrame.current = currentFrame;
    lastDragX.current = e.clientX;
    velocity.current = 0;

    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }
  }, [currentFrame]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;

    const newFrame = calculateFrameFromDrag(e.clientX);
    velocity.current = e.clientX - lastDragX.current;
    lastDragX.current = e.clientX;
    setCurrentFrame(newFrame);
  }, [isDragging, calculateFrameFromDrag]);

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    // Apply momentum if moving fast
    if (Math.abs(velocity.current) > 2) {
      animationFrame.current = requestAnimationFrame(applyMomentum);
    }
  }, [isDragging, applyMomentum]);

  // Touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;

    setIsDragging(true);
    setIsAutoRotating(false);
    dragStartX.current = e.touches[0].clientX;
    dragStartFrame.current = currentFrame;
    lastDragX.current = e.touches[0].clientX;
    velocity.current = 0;

    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }
  }, [currentFrame]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging || e.touches.length !== 1) return;

    const newFrame = calculateFrameFromDrag(e.touches[0].clientX);
    velocity.current = e.touches[0].clientX - lastDragX.current;
    lastDragX.current = e.touches[0].clientX;
    setCurrentFrame(newFrame);
  }, [isDragging, calculateFrameFromDrag]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    if (Math.abs(velocity.current) > 2) {
      animationFrame.current = requestAnimationFrame(applyMomentum);
    }
  }, [isDragging, applyMomentum]);

  // Keyboard handler
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      setCurrentFrame(prev => (prev - 1 + totalFrames) % totalFrames);
      setIsAutoRotating(false);
    } else if (e.key === 'ArrowRight') {
      setCurrentFrame(prev => (prev + 1) % totalFrames);
      setIsAutoRotating(false);
    } else if (e.key === ' ') {
      e.preventDefault();
      setIsAutoRotating(prev => !prev);
    }
  }, [totalFrames]);

  // Global mouse event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!enableFullscreen) return;

    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, [enableFullscreen]);

  // Handle fullscreen change
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
    };
  }, []);

  const allLoaded = loadedFrames.size === totalFrames;
  const loadProgress = Math.round((loadedFrames.size / totalFrames) * 100);
  const currentDegrees = Math.round(currentFrame * degreesPerFrame);

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className={`relative select-none outline-none ${isFullscreen ? 'bg-black' : ''} ${className}`}
      style={{ touchAction: 'none' }}
    >
      {/* Main Image Container */}
      <div
        className={`relative aspect-square bg-gray-900 rounded-lg overflow-hidden cursor-grab ${isDragging ? 'cursor-grabbing' : ''}`}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Loading Overlay */}
        {!allLoaded && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900/90 z-10">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500 mb-3" />
            <div className="text-sm text-gray-400">Loading 360° View</div>
            <div className="w-32 h-1 bg-gray-700 rounded-full mt-2 overflow-hidden">
              <div
                className="h-full bg-purple-500 transition-all duration-300"
                style={{ width: `${loadProgress}%` }}
              />
            </div>
            <div className="text-xs text-gray-500 mt-1">{loadProgress}%</div>
          </div>
        )}

        {/* Frame Images - Stack all frames, only show current */}
        {frames.map((url, index) => (
          <img
            key={index}
            src={url}
            alt={`View ${index + 1}`}
            className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-75 ${
              index === currentFrame ? 'opacity-100' : 'opacity-0'
            }`}
            draggable={false}
            loading="eager"
          />
        ))}

        {/* Drag Hint Overlay */}
        {showHint && allLoaded && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="flex items-center gap-2 px-4 py-2 bg-black/60 rounded-full text-white text-sm animate-pulse">
              <Hand className="w-4 h-4" />
              <span>Drag to rotate</span>
              <ChevronLeft className="w-4 h-4" />
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      {showControls && allLoaded && (
        <div className="mt-3 flex items-center justify-between">
          {/* Left: Navigation arrows */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => {
                setCurrentFrame(prev => (prev - 1 + totalFrames) % totalFrames);
                setIsAutoRotating(false);
              }}
              className="p-1.5 bg-gray-800 hover:bg-gray-700 rounded text-white transition-colors"
              title="Previous frame (Left Arrow)"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                setCurrentFrame(prev => (prev + 1) % totalFrames);
                setIsAutoRotating(false);
              }}
              className="p-1.5 bg-gray-800 hover:bg-gray-700 rounded text-white transition-colors"
              title="Next frame (Right Arrow)"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Center: Angle indicator */}
          <div className="flex items-center gap-2 px-3 py-1 bg-gray-800/80 rounded-full">
            <span className="text-xs text-gray-400">
              {currentDegrees}°
            </span>
            <div className="w-px h-3 bg-gray-600" />
            <span className="text-xs text-gray-500">
              Frame {currentFrame + 1}/{totalFrames}
            </span>
          </div>

          {/* Right: Auto-rotate & Fullscreen */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsAutoRotating(!isAutoRotating)}
              className={`p-1.5 rounded transition-colors ${
                isAutoRotating
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-800 hover:bg-gray-700 text-gray-300'
              }`}
              title={isAutoRotating ? 'Stop auto-rotate (Space)' : 'Auto-rotate (Space)'}
            >
              <RotateCw className={`w-4 h-4 ${isAutoRotating ? 'animate-spin' : ''}`} style={{ animationDuration: '2s' }} />
            </button>
            {enableFullscreen && (
              <button
                onClick={toggleFullscreen}
                className="p-1.5 bg-gray-800 hover:bg-gray-700 rounded text-gray-300 transition-colors"
                title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              >
                {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Frame dots indicator */}
      {showControls && allLoaded && totalFrames <= 36 && (
        <div className="mt-2 flex justify-center">
          <div className="flex items-center gap-0.5 px-2 py-1 bg-gray-800/50 rounded-full">
            {frames.map((_, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setCurrentFrame(idx);
                  setIsAutoRotating(false);
                }}
                className={`transition-all ${
                  idx === currentFrame
                    ? 'w-3 h-1.5 bg-purple-500 rounded-full'
                    : 'w-1.5 h-1.5 bg-gray-600 hover:bg-gray-500 rounded-full'
                }`}
                title={`View at ${Math.round(idx * degreesPerFrame)}°`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductSpinViewer;
